import { Button, Popover, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState, type JSX, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { StoreDayGridPendingShipment } from '../../api/types';
import { UNUSUAL_NEGATIVE } from '../../lib/balanceColors';
import { formatKopeks } from '../../lib/money';
import type { ReturnToState } from '../../lib/returnTo';

/**
 * The orange «waiting» line under a sklad cell / total: the running priced sum
 * of the unposted накладные there, or — while nothing is priced yet — the
 * document number itself, so an unpriced накладная is still visible.
 */
export function pendingShipmentsLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  sum: number,
  docs: { msName: string }[],
): string {
  const n = docs.length;
  if (sum > 0)
    return `${t('daygrid.pendingLine', { sum: formatKopeks(sum) })}${n > 1 ? ` (${n})` : ''}`;
  if (n === 1) return t('daygrid.detail.shipmentTag', { name: docs[0]!.msName });
  return t('daygrid.pendingShipments', { n });
}

/**
 * Unposted накладные of one day × sklad column. One document: tapping opens
 * it straight away (its price/post screen), with the jadval as the return
 * address. Several: a popover lists them, each with its own «open».
 */
export function PendingShipmentsCell({
  docs,
  date,
  columnTitle,
  returnTo,
}: {
  docs: StoreDayGridPendingShipment[];
  date: string;
  columnTitle: string;
  /** Where the накладная screen sends the user back to (this jadval). */
  returnTo: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (docs.length === 0) return null;

  const sum = docs.reduce((acc, d) => acc + d.issueTotal, 0);
  const openDoc = (id: string): void => {
    const state: ReturnToState = { returnTo };
    navigate(`/shipments/${id}`, { state });
  };
  const line = (
    <span
      style={{
        color: UNUSUAL_NEGATIVE,
        fontSize: 12,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {pendingShipmentsLabel(t, sum, docs)}
    </span>
  );
  const trigger = (onClick: () => void): JSX.Element => (
    <span
      role="button"
      onClick={(e: MouseEvent) => {
        // The row click opens the day drawer; this line has its own job.
        e.stopPropagation();
        onClick();
      }}
      style={{ cursor: 'pointer', display: 'inline-flex', paddingBlock: 2 }}
    >
      {line}
    </span>
  );

  if (docs.length === 1) return trigger(() => openDoc(docs[0]!.id));

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      destroyTooltipOnHide
      // Body-mounted like the other day-grid popovers (see PendingCellPopover).
      getPopupContainer={() => document.body}
      title={`${dayjs(date).format('DD.MM')} · ${columnTitle} · ${t('daygrid.pendingShipmentsTitle')}`}
      content={
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ display: 'grid', gap: 4, minWidth: 220 }}
        >
          {docs.map((d) => (
            <Button
              key={d.id}
              type="link"
              size="small"
              style={{ justifyContent: 'flex-start', padding: 0, height: 'auto' }}
              onClick={() => openDoc(d.id)}
            >
              <span style={{ display: 'grid', textAlign: 'left', lineHeight: 1.25 }}>
                <span>
                  {t('shipments.rowTitle', { name: d.msName })}
                  {d.issueTotal > 0 ? ` · ${formatKopeks(d.issueTotal)}` : ''}
                </span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('daygrid.pendingShipmentPriced', {
                    priced: d.pricedCount,
                    total: d.itemCount,
                  })}
                </Typography.Text>
              </span>
            </Button>
          ))}
        </div>
      }
    >
      {trigger(() => setOpen(true))}
    </Popover>
  );
}
